import { LegalFooter } from "../legal.js";
import { useState, type ReactNode } from "react";
import { CognitoError, type CognitoPasswordAuth } from "./cognito-password.js";

/**
 * In-house, dark-themed authentication UI (no Cognito Hosted UI, no redirect).
 * A small controller that switches between sign-in, sign-up, and the first-login
 * new-password challenge, driving the CognitoPasswordAuth flow. On a successful
 * sign-in (tokens stored) it calls `onAuthenticated`, and AuthGate takes over
 * with tenant discovery.
 */

type View = "sign-in" | "sign-up" | "confirm-signup" | "forgot" | "reset" | "new-password";

export function AuthScreens(props: { auth: CognitoPasswordAuth; onAuthenticated: () => void; reason?: string; isLocal?: boolean }): ReactNode {
  const { auth } = props;
  const [view, setView] = useState<View>("sign-in");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [info, setInfo] = useState<string | undefined>(props.reason);
  // Carried across steps (reset code screen, first-login new-password challenge,
  // and the sign-up confirmation code screen, which signs in once confirmed).
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [session, setSession] = useState("");

  const go = (v: View, opts?: { info?: string }) => {
    setError(undefined);
    setInfo(opts?.info);
    setView(v);
  };

  /** Run an async auth step with unified busy/error handling. */
  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof CognitoError ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onSignIn = (e: string, p: string) =>
    void run(async () => {
      setEmail(e);
      const r = await auth.signInWithPassword(e, p);
      if (r.status === "new_password_required") {
        setSession(r.session);
        go("new-password");
        return;
      }
      props.onAuthenticated();
    });

  const onSignUp = (e: string, p: string) =>
    void run(async () => {
      setEmail(e);
      setPassword(p);
      // Cognito emails a confirmation code; collect it on the next screen, then
      // sign in. This proves the person owns the email address before access.
      const r = await auth.signUp(e, p);
      go("confirm-signup", { info: `We emailed a confirmation code to ${r.destination ?? e}.` });
    });

  const onConfirmSignUp = (code: string) =>
    void run(async () => {
      await auth.confirmSignUp(email, code);
      await auth.signInWithPassword(email, password);
      props.onAuthenticated();
    });

  const onResendSignUpCode = () =>
    void run(async () => {
      const r = await auth.resendSignUpCode(email);
      setInfo(`We emailed a new code to ${r.destination ?? email}.`);
    });

  const onForgot = (e: string) =>
    void run(async () => {
      setEmail(e);
      const r = await auth.forgotPassword(e);
      go("reset", { info: `We emailed a reset code to ${r.destination ?? e}.` });
    });

  const onReset = (code: string, newPassword: string) =>
    void run(async () => {
      await auth.confirmForgotPassword(email, code, newPassword);
      await auth.signInWithPassword(email, newPassword);
      props.onAuthenticated();
    });

  const onNewPassword = (newPassword: string) =>
    void run(async () => {
      const r = await auth.completeNewPassword(email, newPassword, session);
      if (r.status === "new_password_required") {
        setError("Please choose a different password.");
        return;
      }
      props.onAuthenticated();
    });

  return (
    <Shell>
      {props.isLocal ? <p className="cfo-hint">Local dev: any password signs in; any code resets.</p> : null}
      {info ? <Alert tone="info">{info}</Alert> : null}
      {error ? <Alert tone="error">{error}</Alert> : null}

      {view === "sign-in" && <SignIn busy={busy} onSubmit={onSignIn} onForgot={() => go("forgot")} onSignUp={() => go("sign-up")} />}
      {view === "sign-up" && <SignUp busy={busy} onSubmit={onSignUp} onBack={() => go("sign-in")} />}
      {view === "confirm-signup" && <ConfirmSignUp email={email} busy={busy} onSubmit={onConfirmSignUp} onResend={onResendSignUpCode} onBack={() => go("sign-in")} />}
      {view === "forgot" && <Forgot busy={busy} onSubmit={onForgot} onBack={() => go("sign-in")} />}
      {view === "reset" && <ResetPassword busy={busy} onSubmit={onReset} onBack={() => go("sign-in")} />}
      {view === "new-password" && <NewPassword email={email} busy={busy} onSubmit={onNewPassword} />}
    </Shell>
  );
}

// --- Layout -----------------------------------------------------------------

function Shell({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="cfo-auth-bg">
      <style>{CSS}</style>
      <div className="cfo-card">
        <div className="cfo-brand">
          <img className="cfo-logo" src="/apple-touch-icon.png" alt="" aria-hidden="true" />
          <span className="cfo-wordmark">CoreFinOps</span>
        </div>
        {children}
        <LegalFooter />
      </div>
    </div>
  );
}

function Alert({ tone, children }: { tone: "error" | "info"; children: ReactNode }): ReactNode {
  return (
    <p role="alert" className={`cfo-alert ${tone === "error" ? "cfo-alert-error" : "cfo-alert-info"}`}>
      {children}
    </p>
  );
}

// --- Views ------------------------------------------------------------------

function SignIn({ busy, onSubmit, onForgot, onSignUp }: { busy: boolean; onSubmit: (e: string, p: string) => void; onForgot: () => void; onSignUp: () => void }): ReactNode {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const ok = email.trim() && password && !busy;
  return (
    <form onSubmit={(e) => (e.preventDefault(), ok && onSubmit(email.trim(), password))}>
      <h1 className="cfo-title">Welcome back</h1>
      <p className="cfo-sub">Sign in to your CoreFinOps workspace.</p>
      <Field label="Email" type="email" autoComplete="username" value={email} onChange={setEmail} disabled={busy} placeholder="you@company.com" />
      <Field label="Password" type="password" autoComplete="current-password" value={password} onChange={setPassword} disabled={busy} placeholder="••••••••" />
      <Submit busy={busy} enabled={!!ok}>Sign in</Submit>
      <div className="cfo-links">
        <button type="button" className="cfo-link" onClick={onForgot}>Forgot password?</button>
        <button type="button" className="cfo-link" onClick={onSignUp}>Create an account</button>
      </div>
    </form>
  );
}

function Forgot({ busy, onSubmit, onBack }: { busy: boolean; onSubmit: (e: string) => void; onBack: () => void }): ReactNode {
  const [email, setEmail] = useState("");
  const ok = email.trim() && !busy;
  return (
    <form onSubmit={(e) => (e.preventDefault(), ok && onSubmit(email.trim()))}>
      <h1 className="cfo-title">Reset password</h1>
      <p className="cfo-sub">Enter your email and we’ll send a reset code.</p>
      <Field label="Email" type="email" autoComplete="username" value={email} onChange={setEmail} disabled={busy} placeholder="you@company.com" />
      <Submit busy={busy} enabled={!!ok}>Send reset code</Submit>
      <div className="cfo-links cfo-links-center">
        <button type="button" className="cfo-link" onClick={onBack}>Back to sign in</button>
      </div>
    </form>
  );
}

function ResetPassword({ busy, onSubmit, onBack }: { busy: boolean; onSubmit: (code: string, pw: string) => void; onBack: () => void }): ReactNode {
  const [code, setCode] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const mismatch = confirm.length > 0 && pw !== confirm;
  const ok = code.trim() && pw && pw === confirm && !busy;
  return (
    <form onSubmit={(e) => (e.preventDefault(), ok && onSubmit(code.trim(), pw))}>
      <h1 className="cfo-title">Choose a new password</h1>
      <p className="cfo-sub">Enter the code from your email and a new password.</p>
      <Field label="Reset code" type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} disabled={busy} placeholder="123456" />
      <Field label="New password" type="password" autoComplete="new-password" value={pw} onChange={setPw} disabled={busy} placeholder="At least 12 characters" />
      <Field label="Confirm new password" type="password" autoComplete="new-password" value={confirm} onChange={setConfirm} disabled={busy} placeholder="Re-enter password" />
      <p className="cfo-hint-sm">12+ characters with uppercase, lowercase, a number, and a symbol.</p>
      {mismatch ? <p className="cfo-mismatch">Passwords do not match.</p> : null}
      <Submit busy={busy} enabled={!!ok}>Reset password</Submit>
      <div className="cfo-links cfo-links-center">
        <button type="button" className="cfo-link" onClick={onBack}>Back to sign in</button>
      </div>
    </form>
  );
}

function SignUp({ busy, onSubmit, onBack }: { busy: boolean; onSubmit: (e: string, p: string) => void; onBack: () => void }): ReactNode {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const mismatch = confirm.length > 0 && password !== confirm;
  const ok = email.trim() && password && password === confirm && !busy;
  return (
    <form onSubmit={(e) => (e.preventDefault(), ok && onSubmit(email.trim(), password))}>
      <h1 className="cfo-title">Create your account</h1>
      <p className="cfo-sub">Start with CoreFinOps in a minute.</p>
      <Field label="Email" type="email" autoComplete="username" value={email} onChange={setEmail} disabled={busy} placeholder="you@company.com" />
      <Field label="Password" type="password" autoComplete="new-password" value={password} onChange={setPassword} disabled={busy} placeholder="At least 12 characters" />
      <Field label="Confirm password" type="password" autoComplete="new-password" value={confirm} onChange={setConfirm} disabled={busy} placeholder="Re-enter password" />
      <p className="cfo-hint-sm">12+ characters with uppercase, lowercase, a number, and a symbol.</p>
      {mismatch ? <p className="cfo-mismatch">Passwords do not match.</p> : null}
      <Submit busy={busy} enabled={!!ok}>Create account</Submit>
      <div className="cfo-links cfo-links-center">
        <button type="button" className="cfo-link" onClick={onBack}>Already have an account? Sign in</button>
      </div>
    </form>
  );
}

function ConfirmSignUp({ email, busy, onSubmit, onResend, onBack }: { email: string; busy: boolean; onSubmit: (code: string) => void; onResend: () => void; onBack: () => void }): ReactNode {
  const [code, setCode] = useState("");
  const ok = code.trim() && !busy;
  return (
    <form onSubmit={(e) => (e.preventDefault(), ok && onSubmit(code.trim()))}>
      <h1 className="cfo-title">Confirm your email</h1>
      <p className="cfo-sub">
        Enter the code we emailed to <strong className="cfo-strong">{email}</strong> to finish creating your account.
      </p>
      <Field label="Confirmation code" type="text" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={setCode} disabled={busy} placeholder="123456" />
      <Submit busy={busy} enabled={!!ok}>Confirm and continue</Submit>
      <div className="cfo-links">
        <button type="button" className="cfo-link" onClick={onResend} disabled={busy}>Resend code</button>
        <button type="button" className="cfo-link" onClick={onBack}>Back to sign in</button>
      </div>
    </form>
  );
}

function NewPassword({ email, busy, onSubmit }: { email: string; busy: boolean; onSubmit: (pw: string) => void }): ReactNode {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const mismatch = confirm.length > 0 && pw !== confirm;
  const ok = pw && pw === confirm && !busy;
  return (
    <form onSubmit={(e) => (e.preventDefault(), ok && onSubmit(pw))}>
      <h1 className="cfo-title">Set a new password</h1>
      <p className="cfo-sub">
        First sign-in for <strong className="cfo-strong">{email}</strong>. Choose a new password to continue.
      </p>
      <Field label="New password" type="password" autoComplete="new-password" value={pw} onChange={setPw} disabled={busy} placeholder="At least 8 characters" />
      <Field label="Confirm new password" type="password" autoComplete="new-password" value={confirm} onChange={setConfirm} disabled={busy} placeholder="Re-enter password" />
      {mismatch ? <p className="cfo-mismatch">Passwords do not match.</p> : null}
      <Submit busy={busy} enabled={!!ok}>Set password and continue</Submit>
    </form>
  );
}

// --- Primitives -------------------------------------------------------------

function Field(props: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  autoComplete?: string;
  placeholder?: string;
  inputMode?: "numeric";
}): ReactNode {
  const [reveal, setReveal] = useState(false);
  const isPassword = props.type === "password";
  const type = isPassword && reveal ? "text" : props.type;
  return (
    <label className="cfo-field">
      <span className="cfo-label">{props.label}</span>
      <span className="cfo-input-wrap">
        <input
          className={isPassword ? "cfo-input cfo-input-pw" : "cfo-input"}
          type={type}
          value={props.value}
          onChange={(e) => props.onChange(e.target.value)}
          disabled={props.disabled}
          required
          autoComplete={props.autoComplete}
          placeholder={props.placeholder}
          {...(props.inputMode ? { inputMode: props.inputMode } : {})}
        />
        {isPassword ? (
          <button
            type="button"
            className="cfo-eye"
            onClick={() => setReveal((r) => !r)}
            disabled={props.disabled}
            tabIndex={-1}
            aria-label={reveal ? "Hide password" : "Show password"}
            title={reveal ? "Hide password" : "Show password"}
          >
            {reveal ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        ) : null}
      </span>
    </label>
  );
}

function EyeIcon(): ReactNode {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon(): ReactNode {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function Submit({ busy, enabled, children }: { busy: boolean; enabled: boolean; children: ReactNode }): ReactNode {
  return (
    <button type="submit" className="cfo-btn" disabled={!enabled}>
      {busy ? "Please wait…" : children}
    </button>
  );
}

// --- Styles (dark theme) ----------------------------------------------------

const CSS = `
.cfo-auth-bg{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;padding:24px;overflow:auto;
  background:radial-gradient(1100px 560px at 50% -12%,#12263f 0%,#0a0f16 48%,#05070a 100%);font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;}
.cfo-card{width:100%;max-width:400px;background:#0d131b;border:1px solid #1b2733;border-radius:16px;padding:34px 32px;
  box-shadow:0 24px 70px rgba(0,0,0,.6),0 0 0 1px rgba(255,255,255,.02) inset;color:#e8edf2;}
.cfo-brand{display:flex;align-items:center;gap:11px;margin-bottom:26px;}
.cfo-logo{width:36px;height:36px;border-radius:9px;object-fit:cover;box-shadow:0 0 0 1px rgba(255,255,255,.1),0 4px 14px rgba(0,0,0,.45);}
.cfo-wordmark{font-weight:700;font-size:1.1rem;letter-spacing:.2px;color:#f3f7fb;}
.cfo-title{font-size:1.55rem;font-weight:700;margin:0 0 6px;color:#f5f8fc;letter-spacing:-.2px;}
.cfo-sub{color:#8795a3;margin:0 0 22px;font-size:.9rem;line-height:1.45;}
.cfo-strong{color:#cfe0f0;}
.cfo-field{display:block;margin-bottom:16px;}
.cfo-label{display:block;font-size:.78rem;font-weight:600;color:#9fb0bf;margin-bottom:7px;}
.cfo-input-wrap{position:relative;display:block;}
.cfo-input{width:100%;box-sizing:border-box;padding:.68rem .8rem;font-size:.95rem;color:#eef3f8;background:#111922;
  border:1px solid #273441;border-radius:9px;outline:none;transition:border-color .15s,box-shadow .15s,background .15s;}
.cfo-input-pw{padding-right:2.6rem;}
.cfo-input::placeholder{color:#55626f;}
.cfo-input:focus{border-color:#2f81d6;background:#121c27;box-shadow:0 0 0 3px rgba(47,129,214,.22);}
.cfo-input:disabled{opacity:.55;}
.cfo-eye{position:absolute;top:50%;right:8px;transform:translateY(-50%);display:flex;align-items:center;justify-content:center;
  width:30px;height:30px;padding:0;background:none;border:none;border-radius:6px;color:#7f8c99;cursor:pointer;transition:color .15s,background .15s;}
.cfo-eye:hover:not(:disabled){color:#cfe0f0;background:#1a2430;}
.cfo-eye:disabled{opacity:.5;cursor:not-allowed;}
.cfo-btn{width:100%;margin-top:4px;padding:.72rem 1.2rem;font-size:1rem;font-weight:600;color:#fff;
  background:linear-gradient(135deg,#1d6fb8,#2f86d8);border:none;border-radius:9px;cursor:pointer;transition:filter .15s,opacity .15s;}
.cfo-btn:hover:not(:disabled){filter:brightness(1.08);}
.cfo-btn:disabled{opacity:.5;cursor:not-allowed;}
.cfo-links{margin-top:20px;display:flex;justify-content:space-between;gap:12px;}
.cfo-links-center{justify-content:center;}
.cfo-link{background:none;border:none;color:#6fb0ec;cursor:pointer;padding:0;font-size:.85rem;}
.cfo-link:hover{color:#9cccf5;text-decoration:underline;}
.cfo-alert{padding:.6rem .8rem;border-radius:9px;font-size:.85rem;margin:0 0 18px;line-height:1.4;}
.cfo-alert-error{color:#ffb3b3;background:#2a1416;border:1px solid #5a2630;}
.cfo-alert-info{color:#ffe0a0;background:#272014;border:1px solid #5a481f;}
.cfo-hint{color:#7f8c99;background:#0f1620;border:1px dashed #27323f;border-radius:8px;padding:.5rem .7rem;font-size:.78rem;margin:0 0 16px;}
.cfo-mismatch{color:#ff9d9d;font-size:.8rem;margin:-8px 0 12px;}
.cfo-hint-sm{color:#7f8c99;font-size:.76rem;margin:-6px 0 14px;line-height:1.4;}
`;
